import { PROTOCOL_VERSION, parseServerMessage, type BattleFrame, type ClientMessage, type LobbyPlayer, type MatchSnapshot, type ServerMessage } from "../../../buckle-party/src/mergeborn/protocol";
import type { Element, GridPos } from "./model/board";
import type { Reward } from "./model/run";
import { setGameStatus } from "./status";

export type MultiplayerEvent = ServerMessage | { type: "connection" };
type ConnectionStatus = "offline" | "connecting" | "connected" | "reconnecting";
type Pending = { resolve: (match: MatchSnapshot) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> };
const sessionKey = "mergeborn-online-session-v2";

export class Multiplayer {
  private socket: WebSocket | null = null;
  private listeners = new Set<(event: MultiplayerEvent) => void>();
  private pending = new Map<string, Pending>();
  private retries = 0;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private heartbeat?: ReturnType<typeof setInterval>;
  private handshakeTimer?: ReturnType<typeof setTimeout>;
  private frames: BattleFrame[] = [];
  private seenChat = new Set<string>();
  private lastSequence = 0;
  private stateReceivedAt = 0;
  get battleCountdownRemaining(): number {
    const match = this.match;
    if (!match?.battleStartsAt || !match.serverTime || (this.latestFrame?.elapsed ?? 0) > 0) return 0;
    return Math.max(0, match.battleStartsAt - match.serverTime - (performance.now() - this.stateReceivedAt));
  }
  private id = crypto.randomUUID();
  private name = "";
  private active = false;
  status: ConnectionStatus = "offline";
  lobbyPlayers: LobbyPlayer[] = [];
  match: MatchSnapshot | null = null;
  latestFrame: BattleFrame | null = null;
  error: string | null = null;
  get playerId(): string { return this.id; }
  get isActive(): boolean { return this.active; }
  resume(): boolean {
    try {
      const value = JSON.parse(sessionStorage.getItem(sessionKey) ?? "null");
      if (!value || typeof value.id !== "string" || typeof value.name !== "string") return false;
      this.id = value.id; this.join(value.name); return true;
    } catch { return false; }
  }
  join(name: string): void {
    this.leave(); this.name = name.trim(); this.active = true; this.error = null;
    try { sessionStorage.setItem(sessionKey, JSON.stringify({ id: this.id, name: this.name })); } catch { /* session remains playable */ }
    this.connect();
  }
  private connect(): void {
    if (!this.active) return;
    this.status = this.retries ? "reconnecting" : "connecting"; this.notify({ type: "connection" });
    const configured = import.meta.env.VITE_BUCKLE_PARTY_URL as string | undefined;
    const base = new URL(configured ?? "https://bucklechonger.com");
    base.protocol = base.protocol === "http:" ? "ws:" : "wss:";
    base.pathname = "/parties/mergeborn/lobby-v2";
    base.searchParams.set("protocol", String(PROTOCOL_VERSION));
    console.info("Mergeborn multiplayer connecting", { endpoint: base.href, clientProtocol: PROTOCOL_VERSION, attempt: this.retries + 1 });
    const socket = new WebSocket(base); this.socket = socket;
    this.handshakeTimer = setTimeout(() => { if (this.status !== "connected") { this.fail("The multiplayer server did not respond. Check that the updated worker is deployed."); socket.close(); } }, 8000);
    socket.addEventListener("message", event => {
      if (this.socket !== socket || typeof event.data !== "string") return;
      let message: ServerMessage;
      try { message = parseServerMessage(event.data); }
      catch (error) {
        console.error("Mergeborn multiplayer response rejected", { endpoint: base.href, clientProtocol: PROTOCOL_VERSION, response: event.data.slice(0, 512), error });
        clearTimeout(this.handshakeTimer); clearInterval(this.heartbeat); this.heartbeat = undefined;
        this.active = false; this.status = "offline";
        this.rejectPending("The multiplayer response was incompatible. Reload after deploying matching versions.");
        this.fail(error instanceof Error ? error.message : "Invalid multiplayer response.");
        socket.close(1000, "Incompatible server response");
        return;
      }
      this.receive(message);
    });
    socket.addEventListener("open", () => { if (this.socket === socket) console.info("Mergeborn multiplayer WebSocket opened", { endpoint: base.href, clientProtocol: PROTOCOL_VERSION }); });
    socket.addEventListener("error", () => { if (this.socket === socket) {
      console.error("Mergeborn multiplayer WebSocket error", { endpoint: base.href, clientProtocol: PROTOCOL_VERSION, readyState: socket.readyState });
      this.fail("Could not connect to multiplayer. Retrying…");
    } });
    socket.addEventListener("close", event => {
      if (this.socket !== socket) return;
      console.info("Mergeborn multiplayer WebSocket closed", { endpoint: base.href, code: event.code, reason: event.reason, wasClean: event.wasClean, clientProtocol: PROTOCOL_VERSION, retry: this.active });
      if (!this.active) return;
      clearTimeout(this.handshakeTimer); clearInterval(this.heartbeat); this.heartbeat = undefined;
      this.rejectPending("The connection was interrupted. Reconnecting…");
      this.status = "reconnecting"; this.notify({ type: "connection" });
      if (event.code === 4001) { this.fail("This player is already open in another connection."); this.active = false; return; }
      this.retryTimer = setTimeout(() => this.connect(), Math.min(5000, 600 * 2 ** this.retries++));
    });
  }
  private receive(message: ServerMessage): void {
    switch (message.type) {
      case "welcome": this.send({ type: "join", playerId: this.id, name: this.name }); break;
      case "lobby":
        clearTimeout(this.handshakeTimer); this.status = "connected"; this.retries = 0; this.error = null;
        this.lobbyPlayers = message.players;
        if (!this.heartbeat) this.heartbeat = setInterval(() => this.send({ type: "ping" }), 10_000);
        break;
      case "state": {
        const changedRound = this.match?.id !== message.match.id || this.match?.round !== message.match.round;
        if (changedRound) { this.frames = []; this.lastSequence = 0; this.latestFrame = null; }
        this.match = message.match; this.stateReceivedAt = performance.now(); this.error = null;
        if (message.match.battle) this.acceptFrame(message.match.battle);
        if (message.requestId) this.resolveRequest(message.requestId, message.match);
        break;
      }
      case "frame": this.acceptFrame(message.frame); break;
      case "error":
        this.fail(message.message);
        if (message.requestId) {
          const pending = this.pending.get(message.requestId);
          if (pending) { clearTimeout(pending.timer); pending.reject(new Error(message.message)); this.pending.delete(message.requestId); }
        }
        break;
      case "left": this.match = null; this.frames = []; this.latestFrame = null; this.error = message.message; break;
      case "chat":
        if (message.matchId !== this.match?.id || this.seenChat.has(message.id)) return;
        this.seenChat.add(message.id);
        if (this.seenChat.size > 128) this.seenChat.delete(this.seenChat.values().next().value!);
        break;
      case "pong": break;
    }
    this.notify(message);
  }
  private acceptFrame(frame: BattleFrame): void {
    if (!this.match || frame.matchId !== this.match.id || frame.round !== this.match.round || frame.sequence <= this.lastSequence) return;
    this.lastSequence = frame.sequence; this.latestFrame = frame; this.frames.push(frame);
    if (this.frames.length > 100) this.frames.shift();
  }
  takeFrames(): BattleFrame[] { return this.frames.splice(0); }
  send(message: ClientMessage): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message)); return true;
  }
  private command(message: ClientMessage): Promise<MatchSnapshot> {
    if (this.status !== "connected") return Promise.reject(new Error("Wait for multiplayer to reconnect."));
    const requestId = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { this.pending.delete(requestId); this.fail("The server did not acknowledge your move. Reconnecting…"); reject(new Error("The server did not acknowledge your move.")); this.socket?.close(); }, 8000);
      this.pending.set(requestId, { resolve, reject, timer });
      if (!this.send({ ...message, requestId } as ClientMessage)) { clearTimeout(timer); this.pending.delete(requestId); reject(new Error("The multiplayer connection is unavailable.")); }
    });
  }
  challenge(targetId: string): Promise<MatchSnapshot> { return this.command({ type: "challenge", targetId }); }
  choose(element: Element): Promise<MatchSnapshot> { return this.command({ type: "choose", element, ...this.context() }); }
  arrange(from: GridPos, to: GridPos): Promise<MatchSnapshot> { return this.command({ type: "arrange", from, to, ...this.context() }); }
  reward(reward: Reward): Promise<MatchSnapshot> { return this.command({ type: "reward", reward, ...this.context() }); }
  ready(): Promise<MatchSnapshot> { return this.command({ type: "ready", ...this.context() }); }
  attack(): void { if (this.status === "connected" && this.match) this.send({ type: "attack", ...this.context() }); }
  chat(rawText: string): void {
    if (!this.match) return;
    const text = [...rawText.trim().replace(/[\u0000-\u001f\u007f]/g, " ")].slice(0, 160).join("");
    if (!text) return;
    const requestId = crypto.randomUUID();
    if (!this.send({ type: "chat", text, requestId, ...this.context() })) { this.fail("Your chat message could not be sent. Wait for the connection to return."); return; }
    // Show the sender's message immediately; its server echo uses this same ID.
    this.receive({ type: "chat", matchId: this.match.id, id: requestId, name: this.match.own.name, text });
  }
  private context(): { matchId: string; round: number } {
    if (!this.match) throw new Error("There is no active multiplayer match.");
    return { matchId: this.match.id, round: this.match.round };
  }
  subscribe(listener: (event: MultiplayerEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  leave(): void {
    this.send({ type: "leave" }); this.active = false; clearTimeout(this.retryTimer); clearTimeout(this.handshakeTimer); clearInterval(this.heartbeat); this.heartbeat = undefined;
    const socket = this.socket; this.socket = null; socket?.close(); this.rejectPending("You left multiplayer.");
    this.status = "offline"; this.match = null; this.latestFrame = null; this.frames = []; this.lobbyPlayers = []; this.error = null;
    this.seenChat.clear();
    try { sessionStorage.removeItem(sessionKey); } catch { /* optional storage */ }
    this.notify({ type: "connection" });
  }
  private resolveRequest(id: string, match: MatchSnapshot): void {
    const pending = this.pending.get(id);
    if (pending) { clearTimeout(pending.timer); this.pending.delete(id); pending.resolve(match); }
  }
  private rejectPending(message: string): void {
    for (const pending of this.pending.values()) { clearTimeout(pending.timer); pending.reject(new Error(message)); }
    this.pending.clear();
  }
  fail(message: string): void { this.error = message; console.error("Mergeborn multiplayer:", message); setGameStatus("error", message); this.notify({ type: "connection" }); }
  private notify(event: MultiplayerEvent): void {
    for (const listener of this.listeners) {
      try { listener(event); }
      catch (error) {
        console.error("Mergeborn multiplayer UI failed:", error);
        setGameStatus("error", `Multiplayer display failed: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
  }
}
export const multiplayer = new Multiplayer();
